import { IpcRenderer } from 'electron';
import { InfiniteZoomSettings } from "./infinite-zoom-settings";
import { Updatable } from "./updatable";
import { UpdateDispatcher } from "./update-dispatcher";
import { CanvasRenderer } from "./canvas-renderer";
import { DOMHelpers } from "../helpers/domhelpers";
import { ZoomImage } from "./zoom-image";
import { Point, Rectangle } from "./geometry";
import { ImageManager } from "./image-manager";
import { ResourceStatus, ApplicationState, Phase } from "./enums";
import { URLHelper } from "../helpers/url-helper";
import { SpriteManager } from "./sprite-manager";
import { ScreenRectangle } from "./screen-rectangle";
import { ZoomImageTile } from "./zoom-image-tile";
import { Time } from "./time";
import { ZoomImageDefinition } from "./zoom-image-definition";
import { GUIController } from "./gui-controller";
import { Loader } from "./loader";
import { Random } from "../helpers/random";

export class InfiniteZoom implements Updatable
{
    public static all:InfiniteZoom[] = [];

    public container: HTMLElement;
    public width: number;
    public height: number;

    private settings:InfiniteZoomSettings;
    private renderer:CanvasRenderer;

    private images:ImageManager = new ImageManager();
    private sprites:SpriteManager = new SpriteManager();

    get zoom() {return this._zoom;}
    private _zoom:number = 1;    
    
    private currentImage:ZoomImage = null;
    private nextImage:ZoomImage = null;

    private status:ResourceStatus = ResourceStatus.Empty;

    private _phase:Phase = Phase.Original;
    public get phase():Phase { return this._phase;}
    public set phase(val:Phase) { /*if (val !=this._phase) console.log(`Entering phase ${val}`);*/ this._phase = val;}

    private isIdle:boolean = false;
    private idleCounter:number = 0;
    private idleTarget:Point;
    private suspendCounter:number = 0;

    /**
     * Rectangle matching the viewport element's client rectangle. All images have their rectangles as children of this rectangle
     */
    private viewport:ScreenRectangle;

    private refocusFromPosition:Point;
    private refocusToPosition:Point;    
    private refocusFromScale:Point;
    private refocusToScale:Point;
    private refocusCounter:number = 0;
    private focusTile:ZoomImageTile = null;

    private gui:GUIController;

    private hammer:HammerManager;
    private zoomOnPinchStart:number = 1;

    private mouseDown:boolean = false;
    private mousePos:Point;

    private ipc:IpcRenderer = null;

    constructor (el:HTMLElement, params:InfiniteZoomSettings)    
    {        
        this.settings = params;
        Loader.simultaneous = this.settings.simultanouesDownloads;
        this.container = el;
        this.viewport = new ScreenRectangle();
        UpdateDispatcher.add(this);
        this.resize();

        this.hammer = new Hammer(this.container);
        this.hammer.get('pinch').set({ enable: true });
        this.hammer.on('pinch', (ev) => {this.onPinch(ev);});
        this.hammer.on('pinchstart', (ev) => {this.onPinchStart(ev);});

        this.hammer.get('rotate').set({ enable: false });

    }

    start(): void
    {
        this.gui = new GUIController(this.container);
        let canvas = document.createElement("canvas");
        this.container.appendChild(canvas);        
        this.container.appendChild(this.gui.element);

        canvas.style.width = "100%";
        canvas.style.height = "100%";        
        this.renderer = this.sprites.renderer = new CanvasRenderer(canvas);    

        this.container.onwheel = (ev) => {this.onWheel(ev);};
        this.container.onclick = (ev) => {this.onClick(ev);};
        this.container.onmousedown = (ev) => {this.onMouseDown(ev);}
        this.container.onmouseup = (ev) => {this.onMouseUp(ev);}
        this.container.onmouseout = (ev) => {this.onMouseOut(ev);}
        this.container.onmousemove = (ev) => {this.onMouseMove(ev);}

        this.connectElectron();

        this.bootloader();

        // this.images.each( (img:ZoomImage) => { img.onload( (target, e) => { this.onImageLoaded(target, e) })});
        // this.currentImage = this.images.get(0);
        // this.nextImage = this.images.get(1);
        // this.currentImage.load();
    }

    close()
    {
        console.log('Closing');
        this.ipc.send('close');
    }

    private bootloader()
    {
        this.status = ResourceStatus.DefinitionLoading;
        this.images.loadDB(URLHelper.Absolute("ksnm/images.json"));    
        this.gui.showLoader();    
    }

    update(): void
    {        
        this.images.update();
        this.sprites.upload();
        this.gui.update();
        Loader.Update();

        if (this.images.status == ResourceStatus.Error)
            this.status == ResourceStatus.Error;

        switch (this.status)
        {
            case ResourceStatus.Empty:
                break;

            case ResourceStatus.DefinitionLoading:

                if (this.images.status >= ResourceStatus.DefinitionLoaded)
                {
                    this.status = ResourceStatus.DefinitionLoaded;

                    console.log(`Image database loaded. Total images ${this.images.length}`);

                    this.images.each( (img:ZoomImage) => { 
                    
                        img.sprites = this.sprites;
                        img.fadeStart = this.settings.crossfadeStart;
                        img.fadeEnd = this.settings.crossfadeEnd;
                        img.viewport = this.viewport;
                        img.imageManager = this.images;
                        img.spriteManager = this.sprites;
                        img.fit(this.width, this.height);
                        this.viewport.addChild(img.rect);
                    });

                    if (this.settings.start >= 0)
                    {
                        if (this.settings.start < this.images.length)
                        {
                            this.setFirstImage(this.images.get(this.settings.start));               
                        }
                        else
                        {
                            this.setFirstImage(this.images.get(0));               
                        }
                    }
                    else
                    {
                        this.setFirstImage(this.images.get(Random.Index(this.images.length)));               
                    }    
                    
                    this.status = ResourceStatus.Loaded;
                    this.gui.hideLoader();
                    this.gui.showHint();
                }             
                else
                {
                    this.gui.setLoader( Math.floor(this.images.imageDefinitionsLoaded / this.images.imageDefinitionsToLoad * 100));
                }   
                break;

            case ResourceStatus.Loaded:

                if (!this.isIdle && this.settings.idle > 0)
                {
                    this.idleCounter += Time.deltaTime;
                    if (this.idleCounter > this.settings.idle)
                    {
                        this.startIdle();
                    }
                }

                if (this.ipc && this.settings.suspend > 0)
                {
                    this.suspendCounter += Time.deltaTime;
                    if (this.suspendCounter >= this.settings.suspend)
                    {
                        this.close();
                    }
                }
                
                

                this.renderer.clear("#000000");

                if (this.isIdle)
                {
                    if (!this.currentImage.rect.rect.contains(this.idleTarget))
                    {
                        this.newIdleTarget();
                    }

                    this.setZoom(this.zoom * (1 + this.settings.zoomspeed / 6), this.idleTarget);
                }

                if (this.mouseDown)
                {
                    this.setZoom(this.zoom * (1 + this.settings.zoomspeed / 3), this.mousePos);
                    this.gui.hideHint();
                }
                
                if (this.phase == Phase.WaitForRefocus)
                {
                    // Check if visible tiles are loaded
                    let tilesLoaded:boolean = true;
                    this.currentImage.eachTile( (t) => {
                        if (this.currentImage.tileVisible(t))
                        {
                            if (t.sprite.status != ResourceStatus.Loaded)
                            {
                                tilesLoaded = false;
                            }                            
                        }
                    });

                    if (tilesLoaded && this.nextImage.original.status == ResourceStatus.Loaded)
                    {
                        this.startRefocus();
                    }
                }
                if (this.phase == Phase.Refocus)
                {
                    this.updateRefocus2();
                }


                // Render sprites
                this.sprites.render();

                break;

            case ResourceStatus.Error:
                break;
        }
    }

    private startIdle()
    {
        if (!this.isIdle)
        {
            this.newIdleTarget();
            this.isIdle = true;
            this.idleCounter = 0;
        }
    }

    private stopIdle()
    {
        this.idleCounter = 0;
        this.isIdle = false;
    }

    resize(): void
    {
        if (this.phase == Phase.Refocus || this.phase == Phase.WaitForRefocus) 
            return;            

        this.width = this.viewport.screenWidth = this.container.clientWidth;  
        this.height = this.viewport.screenHeight = this.container.clientHeight;  
        this.viewport.screenPosition = new Point(this.width / 2, this.height / 2);
        this.renderer.resize(this.width, this.height); 

        // Update sprite sizes
        if (this.status >= ResourceStatus.Loaded)
        {
            this.images.each( (img:ZoomImage) => { img.fit(this.width, this.height);})
        }        
    }

    private onWheel(ev:Event)
    {
        this.stopIdle();

        let evt:WheelEvent  = ev as WheelEvent;
        let pos:Point = DOMHelpers.pageToElement(this.container, evt.pageX, evt.pageY);        
        let delta = evt.deltaY;
        //let factor = delta > 0 ? 1 + this.settings.zoomspeed : 1 - this.settings.zoomspeed;
        let factor = 1 + this.settings.zoomspeed;
        this.setZoom(factor * this.zoom, pos);    

        this.gui.hideHint();
    }
   
    private onPinchStart(ev:HammerInput)
    {
        this.stopIdle();
        this.zoomOnPinchStart = this.zoom;
    }

    private onPinch(ev:HammerInput)
    {
        let newZoom = this.zoomOnPinchStart * (1 + (ev.scale - 1) / this.settings.pinchThrottle); 
        this.setZoom(newZoom, new Point(ev.center.x, ev.center.y));
        this.gui.hideHint();
    }

    private onClick(ev:Event)
    {
        this.stopIdle();
    }

    private onMouseDown(ev:Event)
    {
        this.stopIdle();
        let evt:MouseEvent = ev as MouseEvent;
        if (evt.button == 0)
        {
            this.mousePos = DOMHelpers.pageToElement(this.container, evt.pageX, evt.pageY);        
            this.mouseDown = true;    
        }
    }

    private onMouseUp(ev:Event)
    {
        this.mouseDown = false;
    }

    private onMouseOut(ev:Event)
    {
        this.mouseDown = false;
    }

    private onMouseMove(ev:Event)
    {
        if (this.mouseDown)
        {
            let evt:MouseEvent = ev as MouseEvent;
            this.mousePos = DOMHelpers.pageToElement(this.container, evt.pageX, evt.pageY);            
        }
    }

    /**
     * The core method. This attempts to set the zoom to the new value. Depending on the current phase of the zoom various logic is applied.
     * @param zoom 
     * @param center 
     */
    private setZoom(zoom:number, center:Point = null)
    {
        if (zoom < 1) zoom = 1;
        
        if (zoom < this.zoom) zoom = this.zoom;
        
        // make sure center is inside current image rectangle
        if (this.currentImage.rect.rect.contains(center))
        {
            switch (this.phase)
            {
                case Phase.Original: this.ZoomInOriginal(zoom, center); break;
                case Phase.Crossfade: this.ZoomInCrossfade(zoom, center); break;
                case Phase.Mosaic: this.ZoomInMosaic(zoom, center); break;
                case Phase.Refocus: /* No zooming in refocus */ break;
            }    
        }
    } 

    private ZoomInOriginal(zoom:number, center:Point)
    {
        if (zoom > this.settings.crossfadeStart)
        {
            this.phase = Phase.Crossfade;    
            this.setZoom(zoom, center);
        }
        else if (this.currentImage)
        {
            this.currentImage.setZoom(zoom, center);                     
            this._zoom = zoom;
        }        
    }

    private ZoomInCrossfade(zoom:number, center:Point)
    {
        if (zoom > this.settings.crossfadeEnd)
        {
            this.phase = Phase.Mosaic;    
            this.gui.hideInfo(this.settings.hideInfoDuration);
            this.setZoom(zoom, center);
        }
        else
        {
            this.currentImage.setZoom(zoom, center);                     
            this._zoom = zoom;
        }       
    }

    private ZoomInMosaic(zoom:number, center:Point)
    {
        if (zoom > this.settings.refocusStart)
        {
            // Check if all visible tiles in current image are loaded
            let allTilesLoaded = true;
            this.currentImage.eachTile( (t) => {if (t.sprite && t.sprite.status != ResourceStatus.Loaded) { allTilesLoaded = false}});
            this.focusTile = this.currentImage.tileInFocus(center);

            if (this.focusTile != null)
            {
                this.setNextImage( this.focusTile.file);    

                if (allTilesLoaded)
                {
                    this.startRefocus();
                }
                else
                {
                    this.phase = Phase.WaitForRefocus;
                }    
            }
        }
        else
        {
            this.currentImage.setZoom(zoom, center);                     
            this._zoom = zoom;
        }
    }

    private startRefocus()
    {
        // Calculate refocus trajectory
        //this.calculateRefocus(this.currentImage, this.nextImage, tile);
        this.calculateRefocus2(this.currentImage, this.nextImage,  this.focusTile);

        // Align nextimage with focus tile
        this.nextImage.rect.localPosition = this.refocusFromPosition;
        this.nextImage.rect.localScale = this.refocusFromScale;
        this.nextImage.rect.recalculate();
        this.nextImage.original.opacity = 0;
        
        // Hide current image, leave only tiles
        this.sprites.remove(this.currentImage.original);
        this.sprites.remove(this.currentImage.mosaic);

        // Mark all tiles for disappearing except the focus tile and move them all to next image's hierarchy
        this.currentImage.eachTile( (t:ZoomImageTile) => { 

            if (t.sprite)
            {
                if (t != this.focusTile) 
                {
                    t.hideAfter = Math.random() * this.settings.refocusDuration * 0.25;
                }   
                t.sprite.rect.changeParent(this.nextImage.rect);
            }

        });        
        this.phase = Phase.Refocus;
        this.refocusCounter = 0;
    }

    private setFirstImage(img:ZoomImage)
    {
        this.currentImage = img;
        this.images.preloadImage(this.currentImage);
        this.gui.setInfo(img.definition);
        this.gui.showInfo(this.settings.showInfoDuration);
        this.sprites.add(this.currentImage.original);
        this.sprites.add(this.currentImage.mosaic);
    }

    private setNextImage(filename:string)
    {        
        // Find image in db and set it
        this.nextImage = this.images.find(filename);

        this.images.preloadImage(this.nextImage);

        this.nextImage.rect.localScale = new Point(1,1);
        this.nextImage.rect.localPosition = new Point(0.5, 0.5);
        this.nextImage.fit(this.width, this.height);
        
        this.sprites.add(this.nextImage.original);        
        this.sprites.add(this.nextImage.mosaic);
        this.nextImage.original.opacity = 0;
        this.nextImage.mosaic.opacity = 0;      
        
        this.newIdleTarget();
    }

    private newIdleTarget()
    {
        let d = this.width < this.height ? this.width : this.height;
        this.idleTarget = new Point(d * Random.Range(0.25, 0.75), d * Random.Range(0.25, 0.75));
    }

    // /**
    //  * Calculates trajectory of position and zoom of the old image so that the tile in old image is placed over its corresponding part in the new image
    //  */
    // private calculateRefocus(old:ZoomImage, next:ZoomImage, oldTile:ZoomImageTile)
    // {
    //     // UV coordinates of oldtile
    //     let u = 1 / old.mosaicDefinition.tiles_x * oldTile.col;
    //     let v = 1 / old.mosaicDefinition.tiles_y * oldTile.row;

    //     // Starting values
    //     this.refocusFromScale = old.rect.localScale;
    //     this.refocusFromPosition = old.rect.localPosition;
       
    //     // screen rectangle of the next image
    //     let nextRect:Rectangle = next.rect.rect;    

    //     // get the target tile
    //     let targetTile:ZoomImageTile = next.definition.tileByIndex(oldTile.index);

    //     // screen rectangle of the targetTile in next image
    //     let toRect:Rectangle = new Rectangle(targetTile.uw * nextRect.width, targetTile.vh * nextRect.height, nextRect.left + targetTile.u * nextRect.width, nextRect.top + targetTile.v * nextRect.height);

    //     // screen rectangle of the old tile in the old image
    //     let fromRect:Rectangle = oldTile.sprite.rect.rect;

    //     // Target  value for scale
    //     this.refocusToScale = new Point(1,1).mul(this.zoom * toRect.width / fromRect.width);

    //     // Calculate target position:
    //     // Temporarily scale old to the target zoom
    //     let SPO1 = old.rect.screenPosition;
    //     old.rect.localScale = this.refocusToScale;
    //     old.rect.recalculate();        
    //     // screen position of next tile
    //     let SPNT = new Point(toRect.left, toRect.top);
    //     let SPO2 = SPNT.sub( new Point(u - 0.5, v - 0.5).mulXY(old.rect.screenWidth, old.rect.screenHeight));
    //     let diff = SPO2.sub(SPO1);
    //     let diffN = old.rect.parent.screenToLocal(diff);
    //     // Scale back to current zoom
    //     old.rect.localScale = this.refocusFromScale;
    //     old.rect.recalculate();

    //     // Target value for position
    //     this.refocusToPosition = this.refocusFromPosition.add(diffN);        
    // }

    private calculateRefocus2(old:ZoomImage, next:ZoomImage, oldTile:ZoomImageTile)
    {
        let transform:Point = this.align(next, oldTile);

        this.refocusToScale = next.rect.localScale;
        this.refocusToPosition = next.rect.localPosition;
        this.refocusFromScale = new Point(transform.z, transform.z);
        this.refocusFromPosition = new Point(transform.x, transform.y);
    }

    /**
     * Aligns the given image so that the tile's screen area matches the same area in the image. The tile must be part of the image.
     * The image must be fitted inside the current viewport before aligning.
     * 
     * @param img 
     * @param tile 
     * @returns (localPosition.x, localPosition.y, scale) as Point (x,y,z)
     */
    private align(img:ZoomImage, tile:ZoomImageTile):Point
    {
        let result:Point = new Point();

        // Target screen area
        let targetRect:Rectangle = tile.sprite.rect.rect;

        // Source screen area
        let sourceRect:Rectangle = new Rectangle(img.rect.screenWidth * tile.uw, img.rect.screenHeight * tile.vh, img.rect.screenPosition.x + (tile.u - 0.5) * img.rect.screenWidth, img.rect.screenPosition.y + (tile.v - 0.5) * img.rect.screenHeight);

        // targetScale
        result.z = targetRect.width / sourceRect.width;

        // Temporarily scale the image
        img.rect.localScale = new Point(result.z, result.z);
        img.rect.recalculate();

        // Source screen area after scaled up
        let sourceRect2:Rectangle = new Rectangle(img.rect.screenWidth * tile.uw, img.rect.screenHeight * tile.vh, img.rect.screenPosition.x + (tile.u - 0.5) * img.rect.screenWidth, img.rect.screenPosition.y + (tile.v - 0.5) * img.rect.screenHeight);        

        // Offset
        let screenOffset:Point = targetRect.origin.sub(sourceRect2.origin);

        // Target screen position of img
        let TSP:Point = img.rect.screenPosition.add(screenOffset);

        // Target local position of img
        let TLP:Point = img.rect.parent.screenToLocal(TSP);

        result.x = TLP.x;
        result.y = TLP.y;

        // Scale the image back
        img.rect.localScale = new Point(1,1);
        img.rect.recalculate();

        return result;
    }

    // private updateRefocus()
    // {
    //     if (this.nextImage.original.status == ResourceStatus.Loaded)
    //     {
    //         let t = this.refocusCounter / this.settings.refocusDuration;

    //         if (t < 0.5)
    //         {
    //             // Start disappearing tiles if it's their time
    //             this.currentImage.eachTile ( (t:ZoomImageTile) => {
    //                 if (t.hideAfter > 0 && t.hideAfter <= this.refocusCounter)
    //                 {
    //                     t.sprite.fadeOut = 0.05;
    //                     t.sprite.fadeIn = 0;
    //                     t.hideAfter = 0;
    //                 }
    //             });

    //             // Update zoom and focus on old image
    //             this.currentImage.rect.localPosition = this.refocusFromPosition.int(this.refocusToPosition, 2 * t);
    //             this.currentImage.rect.localScale = this.refocusFromScale.int(this.refocusToScale, 2 * t);
    //             this.currentImage.rect.recalculate();
    //         }
    //         else
    //         {
    //             this.currentImage.rect.localPosition = this.refocusToPosition;
    //             this.currentImage.rect.localScale =this.refocusToScale;
    //             this.currentImage.rect.recalculate();
    //             this.nextImage.original.opacity = 2*t - 1;
    //         }


    //         this.refocusCounter += Time.deltaTime;
    //         if (this.refocusCounter >= this.settings.refocusDuration)        
    //         {
    //             this.switchNextImage();
    //         }
    //     }
    // }

    private updateRefocus2()
    {
        let opacityStart:number = 0.4;
        let movementStart:number = 0.3;

        if (this.nextImage.original.status == ResourceStatus.Loaded)
        {
            let t = this.refocusCounter / this.settings.refocusDuration;
            
            // Start disappearing tiles if it's their time
            this.currentImage.eachTile ( (t:ZoomImageTile) => {
                if (t.hideAfter > 0 && t.hideAfter <= this.refocusCounter)
                {
                    t.sprite.fadeOut = 0.05;
                    t.sprite.fadeIn = 0;
                    t.hideAfter = 0;
                }
            });

            // Animate next image appearance
            if (t > opacityStart)
            {
                this.nextImage.original.opacity = (t - opacityStart) / (1 - opacityStart);
            }
            if (t > movementStart)
            {                                    
                this.nextImage.rect.localScale = this.refocusFromScale.int(this.refocusToScale, (t - movementStart) / (1 - movementStart));
                this.nextImage.rect.localPosition = this.refocusFromPosition.int(this.refocusToPosition, (t - movementStart) / (1 - movementStart));
                this.nextImage.rect.recalculate();                        
            }

            this.refocusCounter += Time.deltaTime;

            // Finish transition
            if (this.refocusCounter >= this.settings.refocusDuration)        
            {
                this.switchNextImage();

                if (this.focusTile != null)
                {
                    this.sprites.remove(this.focusTile.sprite);
                    this.focusTile = null;
                }
            }
        }        
    }

    private switchNextImage()
    {
        this.sprites.remove(this.currentImage.original);
        this.sprites.remove(this.currentImage.mosaic);
        this.currentImage.eachTile( (t) => { if (t.sprite) this.sprites.remove(t.sprite);});
        this.currentImage.status = ResourceStatus.DefinitionLoaded;
        this.currentImage.fit(this.width, this.height);

        this.currentImage = this.nextImage;
        this.currentImage.rect.localPosition = new Point(0.5, 0.5);
        this.currentImage.fit(this.width, this.height);        
        this.currentImage.setZoom(1);
        this.nextImage = null;
        this.phase = Phase.Original;
        this._zoom = 1;
        this.refocusCounter = 0;
        this.gui.setInfo(this.currentImage.definition);
        this.gui.showInfo(this.settings.showInfoDuration);
    }

    private connectElectron()
    {
        // Check if Electron is present
        console.log("Looking for Electron IPC");
        if ((<any>window).require)
        {
            try
            {
                this.ipc = (<any>window).require('electron').ipcRenderer;
            } 
            catch (e)
            {
                this.ipc = null;
                //throw e;
                
            }
        } 
        else
        {
            console.warn('Electron\'s IPC was not loaded');
        }

        if (!this.ipc)
        {
            console.log('Electron IPC is not present');
            return;
        }

        // Create home button
        let a = document.createElement("a");
        this.container.appendChild(a);
        a.id = "homebutton";
        a.innerText = "Výber aplikácie";
        a.onclick = () => {this.close();}

        // Get standalone config
        let config = this.ipc.sendSync("get-config");
        if (config)
            this.settings.fill(config);
    }
}